import React, { useEffect, useState, useCallback } from 'react';
import { Box, Container, Typography, Button, CircularProgress, Tooltip, Chip, Avatar, Card, CardContent, IconButton } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { useQueryClient } from 'react-query';
import { useSearchParams, useNavigate, useOutletContext } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import VideocamIcon from '@mui/icons-material/Videocam';
import LocationOnIcon from '@mui/icons-material/LocationOn';

import { ResetPasswordDialog, UploadVideoDialog, ChangeImageDialog } from '../components/dialog';
import EditProfileDialog from '../components/dialog/EditProfileDialog';
import { getUserDetails } from '../utils';
import {
  useMutationChangeProfileVideo,
  useMutationSaveYoutubeLink,
  useMutationChangeProfileImage,
  useMutationChangePassword,
} from '../hooks/react-query/useData';
import { ProfileDataProvider, useProfileDataContext } from '../components/ProfileDataProvider';
import { ROLES } from '../constants';

const MyEdifierProfileContent = () => {
  const [searchParams] = useSearchParams();

  const queryClient = useQueryClient();

  // Use the new profile data management system
  const { profileData, updateProfileData, refreshProfile } = useProfileDataContext();

  const userDetails = profileData || getUserDetails();
  const userToken = JSON.parse(localStorage.getItem('user'))?.token;
  const pairedUsers = useOutletContext();
  const isCurrentUserProfile = window.location.href.includes('my-profile');
  const userRole = userDetails?.role
  const thisUserRole = searchParams.get('role');
  const notManagementRole = userDetails?.role !== 'ADMIN' && userDetails?.role !== 'SITE-LEADER';

  const allRoleData =
    thisUserRole === 'teacher'
      ? searchParams.get('pairedUsers')
        ? queryClient.getQueryData(['getPairedEdifiers'], { exact: false })?.data?.data
        : queryClient.getQueryData(['getCoachesList'], { exact: false })?.data?.data
      : queryClient.getQueryData(['getPairedEdifiers'], { exact: false })?.data?.data;

  const myRoleData = isCurrentUserProfile
    ? [userDetails]
    : allRoleData
      ? allRoleData?.filter((item) => item?._id === searchParams.get('id'))
      : [];



  const profileTitle = isCurrentUserProfile
    ? 'My Profile'
    : `${myRoleData?.[0]?.fname} ${myRoleData?.[0]?.lname}'s Profile`;

  const [openChangePassword, setOpenChangePassword] = useState(false);
  const [openChangeImage, setOpenChangeImage] = useState(false);
  const [openEditProfile, setOpenEditProfile] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);

  const [previewVideo, setPreviewVideo] = useState('');
  const [selectedVideo, setSelectedVideo] = useState(userDetails?.video || '');

  const [openUploadVideo, setOpenUploadVideo] = useState(false);

  const [imageLoaded, setImageLoaded] = useState(false);

  const navigate = useNavigate();

  const { mutate: changeVideo, isLoading: isVideoUpdating } = useMutationChangeProfileVideo(
    toast,
    userToken,
    navigate,
    setPreviewVideo,
    setSelectedVideo,
    setOpenUploadVideo,
    queryClient,
    updateProfileData,
    refreshProfile
  );

  const { mutate: saveYoutubeLinkMutate, isLoading: isYoutubeLinkSaving } = useMutationSaveYoutubeLink(
    toast,
    userToken,
    navigate,
    setOpenUploadVideo,
    queryClient,
    updateProfileData,
    refreshProfile
  );

  const { mutate: changeImage, isLoading: isImageUpdating } = useMutationChangeProfileImage(
    toast,
    userToken,
    navigate,
    () => setOpenChangeImage(false),
    queryClient,
    updateProfileData,
    refreshProfile
  );

  const { mutate: changePassword, isLoading: isPasswordUpdating } = useMutationChangePassword(
    toast,
    userToken,
    navigate,
    () => setOpenChangePassword(false)
  );

  // const [rateCoachOpen, setRateCoachOpen] = useState(false);

  useEffect(() => {
    if (Object.keys(myRoleData).length <= 0) {
      const destination = thisUserRole === 'teacher' ? '/dashboard/find-edifiers' : '/dashboard/teachers';
      navigate(destination);
    }
  }, []);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    // Handle image load error here, if needed
    setImageLoaded(true); // Set to true to hide loader even if there's an error
  };

  const handlePasswordDialogClose = () => {
    setOpenChangePassword(false);
  };

  const saveVideo = () => {
    if (selectedVideo.size > 100 * 1024 * 1024) {
      toast.error('Please make sure Video size is lesser than 100MB');
      return;
    }
    if (!(selectedVideo instanceof File)) {
      toast.error('Please select a new video to upload');
      return;
    }
    const formData = new FormData();
    formData.append('video', selectedVideo);

    changeVideo(formData);
  };

  const saveYoutubeLink = (youtubeUrl) => {
    saveYoutubeLinkMutate(youtubeUrl);
  };

  const onDropVideo = useCallback(
    (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles && rejectedFiles.length > 0) {
        toast.error('Please upload a valid video file (MP4, MOV, AVI, or WebM)');
        return;
      }

      const videoFile = acceptedFiles[0];
      if (videoFile) {
        if (videoFile.size > 100 * 1024 * 1024) {
          toast.error('Video size should not exceed 100MB');
          return;
        }
        setSelectedVideo(videoFile);
        setPreviewVideo(URL.createObjectURL(videoFile));
        setOpenUploadVideo(true);
      }
    },
    []
  );

  const { getRootProps: getVideoRootProps, getInputProps: getVideoInputProps, isDragActive: isVideoDragActive } = useDropzone({
    accept: {
      'video/mp4': ['.mp4'],
      'video/mov': ['.mov'],
      'video/avi': ['.avi'],
      'video/webm': ['.webm'],
    },
    onDrop: onDropVideo,
    multiple: false,
    maxSize: 100 * 1024 * 1024, // 100MB
    noClick: !isCurrentUserProfile, // Disable click if not current user
  });


  return (
    <Box>
      <UploadVideoDialog
        video={userDetails?.video}
        setPreviewFile={setPreviewVideo}
        selectedFile={selectedVideo}
        previewFile={previewVideo}
        setSelectedFile={setSelectedVideo}
        open={openUploadVideo}
        handleClose={() => setOpenUploadVideo(false)}
        isLoading={isVideoUpdating || isYoutubeLinkSaving}
        saveVideo={saveVideo}
        saveYoutubeLink={saveYoutubeLink}
      />
      <ResetPasswordDialog
        open={openChangePassword}
        handleClose={handlePasswordDialogClose}
        isLoading={isPasswordUpdating}
        onSubmit={(payload) => changePassword(payload)}
      />
      <ChangeImageDialog
        open={openChangeImage}
        handleClose={() => setOpenChangeImage(false)}
        isLoading={isImageUpdating}
        onSubmit={(formData) => changeImage(formData)}
      />
      <EditProfileDialog
        open={openEditProfile}
        handleClose={async () => {
          setOpenEditProfile(false);
          // Refresh profile after closing edit dialog in case profile was updated
          if (refreshProfile) {
            await refreshProfile();
          }
        }}
        userRole={userDetails?.role?.toLowerCase()}
        userToEdit={userToEdit}
        setUserToEdit={setUserToEdit}
        userToken={userToken}
        queryClient={queryClient}
      />
      {/* <RateCoach open={rateCoachOpen} setOpen={setRateCoachOpen} /> */}
      <Helmet>
        <title> Edifiying Teacher | {profileTitle} </title>
      </Helmet>

      <Container maxWidth="xl" sx={{ p: '0 !important' }}>
        <Typography
          variant="h3"
          sx={{ color: 'text.titles', display: 'block', mb: '20px', ml: { xs: '20px', sm: '0' } }}
        >
          {profileTitle}
        </Typography>

        <Card
          sx={{
            borderRadius: 3,
            boxShadow: (theme) => theme.palette.mode === 'dark' ? '0px 4px 20px rgba(0, 0, 0, 0.4)' : '0px 4px 20px rgba(0, 0, 0, 0.08)',
            bgcolor: 'background.paper',
            overflow: 'hidden',
            transition: 'box-shadow 0.3s ease-in-out',
            '&:hover': {
              boxShadow: (theme) => theme.palette.mode === 'dark' ? '0px 6px 30px rgba(0, 0, 0, 0.5)' : '0px 6px 30px rgba(0, 0, 0, 0.12)',
            },
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 4,
              }}
            >
              {/* Profile Image Section */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minWidth: { xs: '100%', md: '280px' },
                  maxWidth: { xs: '100%', md: '280px' },
                }}
              >
                {!imageLoaded && (
                  <Box
                    sx={{
                      width: { xs: 200, sm: 250 },
                      height: { xs: 200, sm: 250 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      border: '4px solid',
                      borderColor: 'background.paper',
                      boxShadow: (theme) => theme.palette.mode === 'dark' ? '0px 4px 16px rgba(0, 0, 0, 0.4)' : '0px 4px 16px rgba(0, 0, 0, 0.1)',
                      bgcolor: 'background.neutral',
                    }}
                  >
                    <CircularProgress />
                  </Box>
                )}
                <Box
                  sx={{
                    position: 'relative',
                    display: 'inline-block',
                    cursor: isCurrentUserProfile ? 'pointer' : 'default',
                    '&:hover .avatar-overlay': {
                      opacity: 1,
                      pointerEvents: 'auto',
                    },
                  }}
                >
                  <Avatar
                    src={myRoleData[0]?.image ? `${myRoleData[0]?.image}?${Date.now()}` : '/assets/ProfileAvatar.png'}
                    alt={myRoleData[0]?.fname}
                    sx={{
                      width: { xs: 200, sm: 250 },
                      height: { xs: 200, sm: 250 },
                      border: '4px solid',
                      borderColor: 'primary.main',
                      boxShadow: (theme) => theme.palette.mode === 'dark' ? '0px 4px 16px rgba(0, 0, 0, 0.4)' : '0px 4px 16px rgba(0, 0, 0, 0.1)',
                      display: imageLoaded ? 'flex' : 'none',
                      fontSize: '4rem',
                      color: 'white',
                    }}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                  >
                    {!myRoleData[0]?.image && myRoleData[0]?.fname?.charAt(0)?.toUpperCase()}
                  </Avatar>
                  {isCurrentUserProfile && (
                    <Box
                      className="avatar-overlay"
                      onClick={() => setOpenChangeImage(true)}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0,
                        transition: 'opacity 0.3s ease-in-out',
                        pointerEvents: 'none',
                      }}
                    >
                      <Tooltip title="Change Profile Image" arrow>
                        <IconButton
                          sx={{
                            bgcolor: 'background.paper',
                            color: 'primary.main',
                            '&:hover': {
                              bgcolor: 'background.paper',
                              transform: 'scale(1.1)',
                            },
                            transition: 'all 0.2s ease-in-out',
                          }}
                        >
                          <CameraAltIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>

                {/* Role Chip */}
                <Chip
                  label={notManagementRole ? (myRoleData[0]?.role === ROLES.EDIFIER ? 'Edifier' : 'Teacher') : 'Admin'}
                  sx={{
                    mt: 2,
                    bgcolor: myRoleData[0]?.role === ROLES.EDIFIER ? 'info.lighter' : 'success.lighter',
                    color: myRoleData[0]?.role === ROLES.EDIFIER ? 'info.dark' : 'success.dark',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    px: 2,
                    py: 1,
                    height: 'auto',
                  }}
                />
              </Box>

              {/* Profile Details Section */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Name and Action Buttons */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: 2,
                    mb: 3,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography
                      variant="h3"
                      sx={{
                        color: 'text.titles',
                        fontWeight: 700,
                        textTransform: 'capitalize',
                        fontSize: { xs: '1.75rem', sm: '2.25rem' },
                      }}
                    >
                      {!myRoleData[0]?.fname
                        ? myRoleData[0]?.email?.split('@')[0]
                        : `${myRoleData[0]?.fname} ${myRoleData[0]?.lname}`}
                      {userRole === 'admin' &&
                        <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, py: '10px', textTransform: 'none', }}>
                          {myRoleData[0]?.email}
                        </Typography>}
                    </Typography>
                    <br />

                  </Box>

                  {/* Profile Action Buttons */}
                  {isCurrentUserProfile && notManagementRole && (
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        color="main"
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          px: 3,
                        }}
                        onClick={() => {
                          setUserToEdit(myRoleData[0]);
                          setOpenEditProfile(true);
                        }}
                      >
                        Edit Profile
                      </Button>
                      <Button
                        variant="outlined"
                        color="main"
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          px: 3,
                        }}
                        onClick={() => setOpenChangePassword(true)}
                      >
                        Change Password
                      </Button>
                    </Box>
                  )}
                </Box>

                {/* Location */}
                {notManagementRole && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <LocationOnIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 500, color: 'text.secondary', textTransform: 'capitalize' }}
                    >
                      {myRoleData[0]?.info?.city || 'NA'}, {myRoleData[0]?.info?.state || 'NA'}
                    </Typography>
                  </Box>
                )}

                {/* Content Speciality */}
                {notManagementRole && <Box>
                  <Typography
                    variant="h5"
                    sx={{ color: 'text.primary', fontWeight: 600, mb: 3, lineHeight: 1.6 }}
                  >
                    Content Specialty: <Box component="span" sx={{ fontWeight: 400, marginLeft: '10px' }}>{myRoleData[0]?.info?.contentSpeciality}</Box>
                  </Typography>

                </Box>}

                {/* Languages Spoken */}
                {notManagementRole && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', mb: 1.5 }}>
                      Languages Spoken
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {myRoleData[0]?.info?.languagesSpoken?.map((language, key) => (
                        <Chip
                          key={key}
                          label={language}
                          sx={{
                            bgcolor: 'primary.lighter',
                            color: 'primary.dark',
                            fontWeight: 500,
                            border: '1px solid',
                            borderColor: 'primary.light',
                          }}
                        />
                      )) || (
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            N/A
                          </Typography>
                        )}
                    </Box>
                  </Box>
                )}

                {/* Expertise */}
                {notManagementRole && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', mb: 1.5 }}>
                      Expertise
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {myRoleData[0]?.info?.expertiesAndResources?.map((item, key) => (
                        <Chip
                          key={key}
                          label={item}
                          sx={{
                            bgcolor: 'primary.lighter',
                            color: 'primary.dark',
                            fontWeight: 500,
                            border: '1px solid',
                            borderColor: 'primary.light',
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Who am I */}
                {profileData?.role === ROLES.EDIFIER && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', mb: 1.5 }}>
                      Who am I?
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                      {myRoleData[0]?.info?.interestBio || 'N/A'}
                    </Typography>
                  </Box>
                )}
                {profileData?.role === ROLES.TEACHER && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', mb: 1.5 }}>
                      Tell us a bit about your 'why' - what motivates your interest in teaching?
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                      {myRoleData[0]?.info?.whatMotivates || 'N/A'}
                    </Typography>
                  </Box>
                )}


                {/* Words of Encouragement */}
                {profileData?.role === ROLES.EDIFIER && (
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', mb: 1.5 }}>
                      Words of Encouragement
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'text.secondary',
                        lineHeight: 1.8,
                        bgcolor: 'background.neutral',
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      {myRoleData[0]?.info?.encouragementAdvice === 'test'
                        ? "This user didn't set up any bio yet"
                        : myRoleData[0]?.info?.encouragementAdvice || 'No Bio Yet'}
                    </Typography>
                  </Box>
                )}
                {profileData?.role === ROLES.TEACHER && (
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', mb: 1.5 }}>
                      what is your goal for this year in Edifying Teachers?
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'text.secondary',
                        lineHeight: 1.8,
                        bgcolor: 'background.neutral',
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      {myRoleData[0]?.info?.interestBio || 'N/A'}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Video Section */}
        {notManagementRole && (
          <Card
            sx={{
              mt: 3,
              borderRadius: 3,
              boxShadow: (theme) => theme.palette.mode === 'dark' ? '0px 4px 20px rgba(0, 0, 0, 0.4)' : '0px 4px 20px rgba(0, 0, 0, 0.08)',
              overflow: 'hidden',
              bgcolor: 'background.paper',
            }}
          >
            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: 'text.titles',
                  }}
                >
                  Introduction Video
                </Typography>
                {isCurrentUserProfile && (
                  <LoadingButton
                    color="main"
                    variant={myRoleData[0]?.video ? 'outlined' : 'contained'}
                    onClick={() => setOpenUploadVideo(true)}
                    loading={isVideoUpdating}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                    }}
                  >
                    {myRoleData[0]?.video ? 'Change Video' : 'Add Video'}
                  </LoadingButton>
                )}
              </Box>

              {myRoleData[0]?.video ? (
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    borderRadius: 2,
                    overflow: 'hidden',
                    backgroundColor: '#000',
                    aspectRatio: '16/9',
                    maxHeight: '500px',
                  }}
                >
                  {(() => {
                    const videoUrl = myRoleData[0]?.video;
                    const youtubePattern = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/\s]{11})/;
                    const youtubeMatch = videoUrl && videoUrl.match(youtubePattern);
                    if (youtubeMatch) {
                      const embedUrl = `https://www.youtube.com/embed/${youtubeMatch[1]}`;
                      return (
                        <iframe
                          width="100%"
                          height="100%"
                          src={embedUrl}
                          title="Introduction Video"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          style={{ display: 'block', width: '100%', height: '100%' }}
                        />
                      );
                    }
                    return (
                      /* eslint-disable-next-line jsx-a11y/media-has-caption */
                      <video
                        controls
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        src={`${videoUrl}?${Date.now()}`}
                      >
                        Your browser does not support the video tag.
                      </video>
                    );
                  })()}
                </Box>
              ) : (
                <Box
                  {...(isCurrentUserProfile ? getVideoRootProps() : {})}
                  sx={{
                    border: '2px dashed',
                    borderColor: isVideoDragActive ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    p: 6,
                    textAlign: 'center',
                    bgcolor: isVideoDragActive ? 'action.selected' : 'background.neutral',
                    minHeight: '300px',
                    aspectRatio: '16/9',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: isCurrentUserProfile ? 'pointer' : 'default',
                    transition: 'all 0.3s ease',
                    '&:hover': isCurrentUserProfile
                      ? {
                        borderColor: 'primary.main',
                        bgcolor: 'action.hover',
                      }
                      : {},
                  }}
                >
                  {isCurrentUserProfile && <input {...getVideoInputProps()} />}
                  <Box
                    sx={{
                      width: 120,
                      height: 120,
                      mb: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      backgroundColor: isVideoDragActive ? 'rgba(169, 75, 156, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                      color: isVideoDragActive ? 'primary.main' : 'text.secondary',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <VideocamIcon sx={{ fontSize: 60 }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                    {isVideoDragActive ? 'Drop your video here' : 'No video uploaded yet'}
                  </Typography>
                  {isCurrentUserProfile && (
                    <>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                        Drag and drop a video file here, or click to browse
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Supports: MP4, MOV, AVI, WebM (Max 100MB)
                      </Typography>
                    </>
                  )}
                  {!isCurrentUserProfile && (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      This user hasn't uploaded an introduction video yet
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {notManagementRole && (
          <Card
            sx={{
              mt: 3,
              borderRadius: 3,
              boxShadow: (theme) => theme.palette.mode === 'dark' ? '0px 4px 20px rgba(0, 0, 0, 0.4)' : '0px 4px 20px rgba(0, 0, 0, 0.08)',
              overflow: 'hidden',
              bgcolor: 'background.default',
            }}
          >
            <CardContent sx={{ p: { xs: 3, sm: 4 }, height: '100%' }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: 'text.titles',
                  mb: 3,
                  pb: 2,
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                }}
              >
                Accolades & Demographics
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 4,
                }}
              >
                {/* Left Column */}
                <Box>
                  {myRoleData[0]?.info?.nbctCertified && (
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                        Nationally Board Certified?
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ color: 'text.titles', textTransform: 'capitalize', pl: 2 }}
                      >
                        {myRoleData[0]?.info?.nbctCertified}
                      </Typography>
                    </Box>
                  )}
                  {myRoleData[0]?.info?.specialAccolades && (
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                        Special Accolades
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'text.titles', pl: 2 }}>
                        {myRoleData[0]?.info?.specialAccolades}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Right Column */}
                <Box>
                  {myRoleData[0]?.info?.ethnoRacial && myRoleData[0]?.info?.ethnoRacial.length > 0 && (
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                        Identity Markers
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, pl: 2 }}>
                        {myRoleData[0]?.info?.ethnoRacial?.map((item, key, array) => (
                          <Typography key={key} variant="body1" sx={{ color: 'text.titles' }}>
                            {item}{key < array.length - 1 ? ',' : ''}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}
                  {myRoleData[0]?.gender && (
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                        Gender
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ color: 'text.titles', textTransform: 'capitalize', pl: 2 }}
                      >
                        {myRoleData[0]?.gender}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ mb: 2.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                      Current Status
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: 'text.titles', textTransform: 'capitalize', pl: 2 }}
                    >
                      {profileData?.totalRoles?.includes('SITE-LEADER') ? 'Site Leader' : userRole === 'COACH' ? ' Edifier' : userRole}
                    </Typography>
                  </Box>
                  {myRoleData[0]?.info?.districtSchool && (
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                        District Type
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ color: 'text.titles', textTransform: 'capitalize', pl: 2 }}
                      >
                        {myRoleData[0]?.info?.districtSchool}
                      </Typography>
                    </Box>
                  )}
                  {myRoleData[0]?.info?.partnerSchool && (
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                        Partner Affiliation
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'text.titles', pl: 2 }}>
                        {myRoleData[0]?.info?.partnerSchool}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}
        {notManagementRole && (
          <Card
            sx={{
              mt: 3,
              borderRadius: 3,
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #8E71AD 0%, #A94B9C 100%)',
            }}
          >
            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
              <Typography
                variant="h5"
                sx={{
                  color: 'white',
                  fontWeight: 700,
                  mb: 2,
                  lineHeight: 1.4,
                }}
              >
                {myRoleData[0]?.role === ROLES.EDIFIER
                  ? 'Why I Mentor?'
                  : 'What are key areas that you would like to concentrate most with your edifier?'}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(255, 255, 255, 0.95)',
                  lineHeight: 1.8,
                  fontWeight: 400,
                }}
              >
                {userRole === ROLES.EDIFIER ? myRoleData[0]?.info?.whatMotivates : myRoleData[0]?.info?.encouragementAdvice || 'NA'}
              </Typography>
            </CardContent>
          </Card>
        )}
      </Container>
    </Box >
  );
};

const MyEdifierProfile = () => (
  <ProfileDataProvider>
    <MyEdifierProfileContent />
  </ProfileDataProvider>
);

export default MyEdifierProfile;
